import React, { useEffect, useState } from 'react';
import { ActivityIcon, AlertTriangleIcon, CheckIcon, DatabaseIcon, ImageIcon, InfoIcon, KeyRoundIcon, RefreshCwIcon, UploadIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Tabs } from '../../components/ui/Tabs';
import { Field, Input, Select, Textarea } from '../../components/ui/Field';
import { PermissionGate } from '../../components/admin/PermissionGate';
import { usePlatformConfig } from '../../contexts/PlatformConfigContext';
import { useGovernance } from '../../contexts/GovernanceContext';
import { useSectionRoute } from '../../hooks/useSectionRoute';
import { TERMINOLOGY, TERM_CONCEPTS, resolveTerm } from '../../data/terminology';
import type { TermConcept, TerminologyOverrides } from '../../types/install';
import { checkAdminSlug, RESERVED_ROUTES } from '../../data/reservedRoutes';
import { ALLOWED_BRANDING_TYPES, MAX_BRANDING_BYTES, SETTINGS_ARE_SERVER_BACKED, databaseHealth, settingsHistory, systemInformation, uploadBranding } from '../../services/platformSettingsStore';
import type { DatabaseHealth, LocalizationSettings, PlatformIdentity, SecuritySettings, SeoSettings, SettingsChange, SettingsSection, SystemInformation } from '../../types/install';

/**
 * Platform settings.
 *
 * Everything the installer asked once is editable here — and two things are
 * deliberately not: database credentials and server secrets. They are absent
 * from the types this page can reach, so their absence is structural rather
 * than a decision this screen makes.
 *
 * Every section saves on its own, behind a stated reason, because a settings
 * write that half-applies could pair a new site title with an old security
 * policy. Nothing publishes on a control interaction.
 */

const SECTIONS = ['Platform', 'Terminology', 'Website & SEO', 'Branding', 'Localisation', 'Security', 'Admin access', 'Database', 'System information'] as const;
type Section = (typeof SECTIONS)[number];
export function PlatformSettings() {
  const [section, setSection] = useSectionRoute<Section>('/admin/settings', SECTIONS);
  const {
    source,
    error,
    refresh
  } = usePlatformConfig();
  return <div className="space-y-6" data-id="element-6697" data-mp="14txbzo494ymb">
      <PageHeader eyebrow="Platform" title="Settings" description="Platform identity, website metadata, branding, localisation, security policy and where administration answers. Changing any of these never needs the front end rebuilt." actions={<Button variant="secondary" icon={<RefreshCwIcon className="h-4 w-4" data-id="element-6700" data-mp="2p8z9zcla2u9y" />} onClick={refresh} data-id="element-6699" data-mp="qv61scsifx3g">
            Reload configuration
          </Button>} data-id="element-6698" data-mp="14r1hk9rm4s2m" />

      <SourceNotice source={source} error={error} data-id="element-6701" data-mp="2l8fesek9m0qg" />

      <Tabs tabs={SECTIONS} value={section} onChange={setSection} label="Settings sections" data-id="element-6702" data-mp="2l5jjkxzsxq5p" />

      {section === 'Platform' && <PlatformSection data-id="element-6703" data-mp="byfz4c97pw1l" />}
      {section === 'Terminology' && <TerminologySection data-id="element-6704" data-mp="bwv98zxuf1a2" />}
      {section === 'Website & SEO' && <SeoSection data-id="element-6705" data-mp="gp69k3whwp35" />}
      {section === 'Branding' && <BrandingSection data-id="element-6706" data-mp="griw6ud77nmj" />}
      {section === 'Localisation' && <LocalizationSection data-id="element-6707" data-mp="kpyhmttmsnxu" />}
      {section === 'Security' && <SecuritySection data-id="element-6708" data-mp="3k64vkjjhynk9" />}
      {section === 'Admin access' && <AdminAccessSection data-id="element-6709" data-mp="3k7ppq81wg4hl" />}
      {section === 'Database' && <DatabaseSection data-id="element-6710" data-mp="3p4ycklxzswp6" />}
      {section === 'System information' && <SystemSection data-id="element-6711" data-mp="3p7c3ciln3mqx" />}

      {section !== 'Database' && section !== 'System information' && <HistoryPanel data-id="element-6712" data-mp="3t5i5fdbj9e5i" />}
    </div>;
}

/* ------------------------------------------------------------------ source */

function SourceNotice({
  source,
  error
}: {
  source: 'server' | 'local' | 'defaults';
  error: string | null;
}) {
  if (source === 'server') {
    return <div className="flex flex-wrap items-center gap-3 rounded-lg border border-ok-200 bg-ok-50 p-4" data-id="element-6713" data-mp="1dedrz31tii2u">
        <Badge tone="ok" icon={<CheckIcon className="h-3.5 w-3.5" data-id="element-6715" data-mp="1c0m7wb8edshl" />} data-id="element-6714" data-mp="1bzb78ka910up">
          Server-backed — published
        </Badge>
        <p className="min-w-0 text-body-sm text-ink-muted" data-id="element-6716" data-mp="171je5mxvb8ox">
          These values come from the configuration service and apply to every visitor.
        </p>
      </div>;
  }
  return <div className="rounded-lg border border-warn-200 bg-warn-50 p-4" data-id="element-6717" data-mp="1gwb90j02fhg0">
      <div className="flex flex-wrap items-center gap-3" data-id="element-6718" data-mp="1uwcd07bmn4zp">
        <Badge tone="warn" icon={<AlertTriangleIcon className="h-3.5 w-3.5" data-id="element-6720" data-mp="1pz2oquzz4zdg" />} data-id="element-6719" data-mp="1pyarlfqqatl2">
          {source === 'local' ? 'This browser only — not published' : 'Development defaults'}
        </Badge>
        <p className="min-w-0 text-body-sm text-ink-muted" data-id="element-6721" data-mp="24p09f0yp36xw">
          {SETTINGS_ARE_SERVER_BACKED ? 'The configuration service did not answer, so this is the last configuration this browser saw. A change saved now reaches this browser only.' : 'No configuration service is configured for this deployment, so a saved change reaches this browser and its tabs only. Another browser, device or private window sees platform defaults.'}
        </p>
      </div>
      {error && <p className="mt-2 text-body-sm text-ink-muted" data-id="element-6722" data-mp="2x7kq947p6n5k">Reported: {error}</p>}
      <p className="mt-2 text-caption text-ink-subtle" data-id="element-6723" data-mp="2j9cn2ek2iply">
        Connect the API origin — see INSTALLATION.md — and the same screens become authoritative for
        every visitor without any change here.
      </p>
    </div>;
}

/* ------------------------------------------------------- section scaffolding */

/**
 * One saveable section. Owns the draft, what differs, the required reason and
 * the warning before leaving with something outstanding — so no individual
 * section has to reimplement any of it.
 */
function SectionForm<T>({
  section,
  permission,
  title,
  description,
  live,
  children,
  validate,
  summarise
}: {
  section: SettingsSection;
  permission: string;
  title: string;
  description: string;
  live: T;
  children: (draft: T, set: (next: T) => void) => React.ReactNode;
  validate?: (draft: T) => string | null;
  summarise: (draft: T) => Array<{
    field: string;
    from: string;
    to: string;
  }>;
}) {
  const {
    save
  } = usePlatformConfig();
  const {
    me,
    logAction
  } = useGovernance();
  const [draft, setDraft] = useState<T>(live);
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const changes = summarise(draft);
  const dirty = changes.length > 0;
  const problem = validate?.(draft) ?? null;

  // A change made elsewhere replaces an untouched draft rather than leaving
  // this editor quietly out of date.
  useEffect(() => {
    if (!dirty) setDraft(live);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live]);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  async function commit() {
    setSaving(true);
    setFailure(null);
    try {
      await save(section, draft, reason.trim(), me.name);
      logAction({
        action: `Changed ${title.toLowerCase()}`,
        resource: 'Platform settings',
        resourceId: section,
        before: changes.map(change => `${change.field}: ${change.from}`).join(' · ') || '—',
        after: changes.map(change => `${change.field}: ${change.to}`).join(' · ') || '—',
        reason: reason.trim()
      });
      setConfirming(false);
      setReason('');
      setSaved(true);
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'The change could not be saved.');
    } finally {
      setSaving(false);
    }
  }
  return <PermissionGate permission={permission} label={`change ${title.toLowerCase()}`} data-id="element-6724" data-mp="8o49v8mihbyj">
      <Panel title={title} description={description} data-id="element-6725" data-mp="3lvgje6yarahz">
        {children(draft, next => {
        setDraft(next);
        setSaved(false);
      })}

        {problem && <p className="mt-4 flex items-start gap-2 text-body-sm text-danger-700" data-id="element-6726" data-mp="3upbf6odj3oky">
            <AlertTriangleIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-6727" data-mp="3qnq1x1x318a8" />
            {problem}
          </p>}

        <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-line pt-4" data-id="element-6728" data-mp="32yx4t24q4ue1">
          {dirty ? <>
              <Badge tone="warn" data-id="element-6729" data-mp="2y0ncczadw7w4">
                {changes.length} unsaved {changes.length === 1 ? 'change' : 'changes'}
              </Badge>
              <p className="min-w-0 flex-1 text-body-sm text-ink-muted" data-id="element-6730" data-mp="36yzadmdky7yl">
                Nothing has changed for anyone yet.
              </p>
              <Button variant="ghost" onClick={() => setDraft(live)} data-id="element-6731" data-mp="r6rv49507p18">
                Discard
              </Button>
              <Button onClick={() => setConfirming(true)} disabled={Boolean(problem)} data-id="element-6732" data-mp="mbci1jetlx5v">
                Save changes
              </Button>
            </> : <p className="text-body-sm text-ink-muted" data-id="element-6733" data-mp="ysna3wgubt5v">
              {saved ? 'Saved and applied.' : 'No outstanding changes.'}
            </p>}
        </div>
      </Panel>

      <Modal open={confirming} onClose={() => setConfirming(false)} title={`Save ${title.toLowerCase()}?`} description="Recorded against your name in the audit log with the previous and new values." footer={<>
            <Button variant="ghost" onClick={() => setConfirming(false)} data-id="element-6735" data-mp="13qykc8b1s5om">
              Cancel
            </Button>
            <Button onClick={commit} loading={saving} disabled={reason.trim().length < 4} data-id="element-6736" data-mp="18pi7gc1dnnik">
              Save changes
            </Button>
          </>} data-id="element-6734" data-mp="pqe51m4ccrzv">
        <dl className="divide-y divide-line rounded-lg border border-line" data-id="element-6737" data-mp="1wdt2uzob0uzg">
          {changes.map(change => <div key={change.field} className="grid gap-1 p-3 sm:grid-cols-[160px_1fr]" data-id="element-6738" data-mp="3rbyvjmug1cfm">
              <dt className="text-body-sm font-semibold text-ink" data-id="element-6739" data-mp="3ra5mk0wcyg4g">{label(change.field)}</dt>
              <dd className="min-w-0 break-words text-body-sm text-ink-muted" data-id="element-6740" data-mp="3bdbfri25ca">
                <span className="line-through" data-id="element-6741" data-mp="3ib0sp7vqraym">{change.from || '—'}</span>{' '}
                <span className="text-ink" data-id="element-6742" data-mp="3n92a7odir27z">→ {change.to || '—'}</span>
              </dd>
            </div>)}
        </dl>
        <div className="mt-4" data-id="element-6743" data-mp="38je84kxijzj7">
          <Field label="Why is this changing?" hint="A reason of substance is required." data-id="element-6744" data-mp="3dbxo1ojob6r6">
            <Textarea rows={3} value={reason} onChange={event => setReason(event.target.value)} data-id="element-6745" data-mp="2zksqqt3s2qk0" />
          </Field>
        </div>
        {failure && <p className="mt-3 text-body-sm text-danger-700" data-id="element-6746" data-mp="33n7d3n6no8g1">{failure}</p>}
      </Modal>
    </PermissionGate>;
}
function label(field: string): string {
  return field.replace(/([A-Z])/g, ' $1').replace(/^./, character => character.toUpperCase()).trim();
}
function diffOf<T extends Record<string, unknown>>(before: T, after: T): Array<{
  field: string;
  from: string;
  to: string;
}> {
  return Object.keys(after).filter(key => String(before[key] ?? '') !== String(after[key] ?? '')).map(key => ({
    field: key,
    from: String(before[key] ?? ''),
    to: String(after[key] ?? '')
  }));
}

/* -------------------------------------------------------------- 1. platform */

function PlatformSection() {
  const {
    config
  } = usePlatformConfig();
  return <SectionForm<PlatformIdentity> section="platform" permission="platform.settings.manage" title="Platform identity" description="Who this deployment is. Used in the interface, in transactional mail and in link previews." live={config.platform} validate={draft => draft.name.trim().length < 2 ? 'A platform name is required.' : null} summarise={draft => diffOf(config.platform as never, draft as never)} data-id="element-6747" data-mp="2p08os133hu5j">
      {(draft, set) => <div className="grid gap-4 md:grid-cols-2" data-id="element-6748" data-mp="3fref7sxfp3fm">
          <Field label="Platform name" data-id="element-6749" data-mp="3forzv74axjmu">
            <Input value={draft.name} onChange={e => set({
          ...draft,
          name: e.target.value
        })} data-id="element-6750" data-mp="3kpg26oxzdmwd" />
          </Field>
          <Field label="Short name" hint="Used where space is tight — navigation, tabs." data-id="element-6751" data-mp="3po120n9bm4ka">
            <Input value={draft.shortName} onChange={e => set({
          ...draft,
          shortName: e.target.value
        })} data-id="element-6752" data-mp="3ug0vbwuvxz7w" />
          </Field>
          <Field label="Organisation" data-id="element-6753" data-mp="2eckfol2s82l">
            <Input value={draft.organization} onChange={e => set({
          ...draft,
          organization: e.target.value
        })} data-id="element-6754" data-mp="7a9vbfdkkm7z" />
          </Field>
          <Field label="Primary domain" data-id="element-6755" data-mp="h8z5sn34z4e7">
            <Input value={draft.domain} onChange={e => set({
          ...draft,
          domain: e.target.value
        })} data-id="element-6756" data-mp="h7xv4jq18jx0" />
          </Field>
          <Field label="Support email" hint="Where students are told to write." data-id="element-6757" data-mp="2e3demo1vqv9f">
            <Input type="email" value={draft.supportEmail} onChange={e => set({
          ...draft,
          supportEmail: e.target.value
        })} data-id="element-6758" data-mp="2j1ezneiv94nj" />
          </Field>
        </div>}
    </SectionForm>;
}

/* ------------------------------------------------------------- 2. seo */

function SeoSection() {
  const {
    config
  } = usePlatformConfig();
  return <SectionForm<SeoSettings> section="seo" permission="platform.settings.manage" title="Website and sharing" description="Applied to the document head at runtime. A change here takes effect on the next load — never a rebuild." live={config.seo} validate={draft => draft.metaDescription.trim().length < 40 ? 'A meta description of at least 40 characters is required — shorter than that is not a description.' : null} summarise={draft => diffOf(config.seo as never, draft as never)} data-id="element-6759" data-mp="mfe10r3jke4f">
      {(draft, set) => <div className="space-y-4" data-id="element-6760" data-mp="3pu1of70aw0bf">
          <div className="grid gap-4 md:grid-cols-2" data-id="element-6761" data-mp="3pww5qpo0p50m">
            <Field label="Website title" data-id="element-6762" data-mp="1hl272nu8t64e">
              <Input value={draft.siteTitle} onChange={e => set({
            ...draft,
            siteTitle: e.target.value
          })} data-id="element-6763" data-mp="1hk0nfuz5vbb2" />
            </Field>
            <Field label="Meta title" hint="Shown in the browser tab and in search results." data-id="element-6764" data-mp="18n025x7e1tbu">
              <Input value={draft.metaTitle} onChange={e => set({
            ...draft,
            metaTitle: e.target.value
          })} data-id="element-6765" data-mp="1dl1iwbhcdgk5" />
            </Field>
            <Field label="Site URL" data-id="element-6766" data-mp="25bzatdbvwx4k">
              <Input value={draft.siteUrl} onChange={e => set({
            ...draft,
            siteUrl: e.target.value
          })} data-id="element-6767" data-mp="25awj536qmqnl" />
            </Field>
            <Field label="Search engines" data-id="element-6768" data-mp="1w9yfwn44in62">
              <Select value={draft.robots} onChange={e => set({
            ...draft,
            robots: e.target.value as SeoSettings['robots']
          })} data-id="element-6769" data-mp="1wcubi4si0zrc">
                <option value="index,follow" data-id="element-6770" data-mp="kx5oxej5q2r2">Allow indexing</option>
                <option value="noindex,nofollow" data-id="element-6771" data-mp="dbjx770zdrof">Do not index (staging)</option>
              </Select>
            </Field>
          </div>
          <Field label="Meta description" data-id="element-6772" data-mp="8cz0viouns59">
            <Textarea rows={3} value={draft.metaDescription} onChange={e => set({
          ...draft,
          metaDescription: e.target.value
        })} data-id="element-6773" data-mp="mdjhq9b7fdw4" />
          </Field>
          <div className="grid gap-4 md:grid-cols-2" data-id="element-6774" data-mp="3pt8hqy4mnumy">
            <Field label="Sharing title" hint="Falls back to the meta title when empty." data-id="element-6775" data-mp="3prnl92yr61mg">
              <Input value={draft.socialTitle} onChange={e => set({
            ...draft,
            socialTitle: e.target.value
          })} data-id="element-6776" data-mp="3ltznzt9l7wre" />
            </Field>
            <Field label="Sharing description" data-id="element-6777" data-mp="3us1jjtajxx7a">
              <Input value={draft.socialDescription} onChange={e => set({
            ...draft,
            socialDescription: e.target.value
          })} data-id="element-6778" data-mp="3738w6l7zwxoa" />
            </Field>
          </div>
        </div>}
    </SectionForm>;
}

/* --------------------------------------------------------- 3. branding */

function BrandingSection() {
  const {
    config
  } = usePlatformConfig();
  return <div className="space-y-6" data-id="element-6779" data-mp="1fr2okf531xm9">
      <SectionForm<SeoSettings> section="branding" permission="platform.settings.manage" title="Branding" description="The logo, favicon and sharing image. Upload a file or point at one already hosted." live={config.seo} summarise={draft => diffOf(config.seo as never, draft as never)} data-id="element-6780" data-mp="1frvmqrpz7ess">
        {(draft, set) => <div className="space-y-6" data-id="element-6781" data-mp="wztdw2jkt2t3">
            <AssetRow kind="logo" title="Platform logo" note="Shown in navigation and on authentication screens." url={draft.logoUrl} onChange={url => set({
          ...draft,
          logoUrl: url
        })} data-id="element-6782" data-mp="wyrpkwdjujtf" />
            <AssetRow kind="favicon" title="Favicon" note="The small icon in the browser tab. PNG or ICO, square." url={draft.faviconUrl} onChange={url => set({
          ...draft,
          faviconUrl: url
        })} data-id="element-6783" data-mp="2cgbpjpi3jknu" />
            <AssetRow kind="social" title="Sharing image" note="Used when a Campuzon link is pasted into a message or a post." url={draft.socialImageUrl} onChange={url => set({
          ...draft,
          socialImageUrl: url
        })} data-id="element-6784" data-mp="3o6k9fn59e0gu" />
          </div>}
      </SectionForm>

      <Panel title="What is accepted" description="Enforced here for your benefit, and again on the server — which is the check that counts." data-id="element-6785" data-mp="398rw91n6po5t">
        <ul className="space-y-2 text-body-sm text-ink-muted" data-id="element-6786" data-mp="359ss3j9a1vff">
          <li data-id="element-6787" data-mp="30afv45ooz7z4">PNG, JPEG, WebP or ICO, up to {(MAX_BRANDING_BYTES / 1024 / 1024).toFixed(0)} MB.</li>
          <li data-id="element-6788" data-mp="13g2ze0eveiye">
            SVG is refused. It is a script container as much as an image format, and a logo is not
            worth an injection route.
          </li>
          <li data-id="element-6789" data-mp="7bw3oatcgr7q">
            Filenames are sanitised and the file is stored by the storage service under a generated
            name, so an uploaded name can never become a path.
          </li>
          {!SETTINGS_ARE_SERVER_BACKED && <li className="text-warn-800" data-id="element-6790" data-mp="3trgtid38yuwg">
              No storage service is reachable, so a chosen file is held in this browser only and is
              not an upload. It will not appear for anyone else.
            </li>}
        </ul>
      </Panel>
    </div>;
}
function AssetRow({
  kind,
  title,
  note,
  url,
  onChange
}: {
  kind: 'logo' | 'favicon' | 'social';
  title: string;
  note: string;
  url: string;
  onChange: (url: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  async function choose(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setFailure(null);
    try {
      const asset = await uploadBranding(kind, file);
      onChange(asset.url);
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'The file could not be used.');
    } finally {
      setBusy(false);
    }
  }
  return <div className="rounded-lg border border-line p-4" data-id="element-6791" data-mp="3886llb1y80v3">
      <div className="flex flex-wrap items-start gap-4" data-id="element-6792" data-mp="389re6v4pd9qb">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-line bg-canvas" data-id="element-6793" data-mp="33hge9s9ypnxr">
          {url ? <img src={url} alt={`${title} preview`} className="max-h-full max-w-full object-contain" data-id="element-6794" data-mp="3hb7mmxbyj1rw" /> : <ImageIcon aria-hidden className="h-6 w-6 text-ink-subtle" data-id="element-6795" data-mp="3r7atgghr7ol6" />}
        </div>
        <div className="min-w-0 flex-1 space-y-3" data-id="element-6796" data-mp="3m87undsdaeln">
          <div data-id="element-6797" data-mp="3m8r383w4nqn0">
            <p className="text-body font-semibold text-ink" data-id="element-6798" data-mp="3m8zk06vdfg3h">{title}</p>
            <p className="text-body-sm text-ink-muted" data-id="element-6799" data-mp="1wu2r83rg8api">{note}</p>
          </div>
          <Field label="Image URL" data-id="element-6800" data-mp="1ruyitfccmh08">
            <Input value={url} onChange={event => onChange(event.target.value)} data-id="element-6801" data-mp="1rvrkw30d4ic1" />
          </Field>
          <div className="flex flex-wrap items-center gap-3" data-id="element-6802" data-mp="3kxp7tx0s8wez">
            <label className="inline-flex min-h-touch cursor-pointer items-center gap-2 rounded-lg border border-line px-3 text-body-sm font-medium text-ink" data-id="element-6803" data-mp="3g17490h6v448">
              <UploadIcon aria-hidden className="h-4 w-4" data-id="element-6804" data-mp="3ur60gxp6o5h2" />
              {busy ? 'Reading…' : 'Choose a file'}
              <input type="file" className="sr-only" accept={ALLOWED_BRANDING_TYPES.join(',')} onChange={event => choose(event.target.files?.[0])} data-id="element-6805" data-mp="320nwzvkjs234" />
            </label>
            {url && <Button variant="ghost" onClick={() => onChange('')} data-id="element-6806" data-mp="3bwr0b4r4y6rn">
                Remove
              </Button>}
          </div>
          {failure && <p className="text-body-sm text-danger-700" data-id="element-6807" data-mp="r7tjsabeywsx">{failure}</p>}
        </div>
      </div>
    </div>;
}

/* ------------------------------------------------------ 4. localisation */

const TIMEZONES = ['Africa/Lagos', 'Africa/Accra', 'Africa/Nairobi', 'Africa/Johannesburg', 'Europe/London', 'UTC'];
function LocalizationSection() {
  const {
    config
  } = usePlatformConfig();
  return <SectionForm<LocalizationSettings> section="localization" permission="platform.settings.manage" title="Localisation" description="How dates, money and measurements are presented. Nigeria-first defaults — defaults only." live={config.localization} summarise={draft => diffOf(config.localization as never, draft as never)} data-id="element-6808" data-mp="kte92xxofsdz">
      {(draft, set) => <div className="grid gap-4 md:grid-cols-2" data-id="element-6809" data-mp="13s0723xwd3do">
          <Field label="Default timezone" data-id="element-6810" data-mp="13pe2md6az3zo">
            <Select value={draft.timezone} onChange={e => set({
          ...draft,
          timezone: e.target.value
        })} data-id="element-6811" data-mp="17s0qy43rm6s4">
              {TIMEZONES.map(zone => <option key={zone} value={zone} data-id="element-6812" data-mp="209d0uymzlbs2">
                  {zone}
                </option>)}
            </Select>
          </Field>
          <Field label="Currency" data-id="element-6813" data-mp="2a2zdfb6yvdr8">
            <Select value={draft.currency} onChange={e => set({
          ...draft,
          currency: e.target.value
        })} data-id="element-6814" data-mp="262youxq5lm6g">
              {['NGN', 'GHS', 'KES', 'ZAR', 'USD', 'GBP'].map(code => <option key={code} value={code} data-id="element-6815" data-mp="1w496z4y9c1q7">
                  {code}
                </option>)}
            </Select>
          </Field>
          <Field label="Locale" data-id="element-6816" data-mp="1r8263qkpxgf4">
            <Select value={draft.locale} onChange={e => set({
          ...draft,
          locale: e.target.value
        })} data-id="element-6817" data-mp="1mf93b88t8i5o">
              {['en-NG', 'en-GB', 'en-US', 'fr-FR'].map(locale => <option key={locale} value={locale} data-id="element-6818" data-mp="1det0m0zw5f1v">
                  {locale}
                </option>)}
            </Select>
          </Field>
          <Field label="Country" data-id="element-6819" data-mp="18f80vocwd2o3">
            <Input value={draft.country} onChange={e => set({
          ...draft,
          country: e.target.value
        })} data-id="element-6820" data-mp="13gn11q1k4l06" />
          </Field>
          <Field label="Date format" data-id="element-6821" data-mp="td80jdc4mcjw">
            <Select value={draft.dateFormat} onChange={e => set({
          ...draft,
          dateFormat: e.target.value as LocalizationSettings['dateFormat']
        })} data-id="element-6822" data-mp="tc6lkxrzp5ww">
              <option value="d MMM yyyy" data-id="element-6823" data-mp="138tapyrcmj4q">17 Aug 2026</option>
              <option value="dd/MM/yyyy" data-id="element-6824" data-mp="188nyhtftuslk">17/08/2026</option>
              <option value="MM/dd/yyyy" data-id="element-6825" data-mp="amguaknfr29q">08/17/2026</option>
              <option value="yyyy-MM-dd" data-id="element-6826" data-mp="ao1qeepeet9p">2026-08-17</option>
            </Select>
          </Field>
          <Field label="Time format" data-id="element-6827" data-mp="fll2p92jcvz0">
            <Select value={draft.timeFormat} onChange={e => set({
          ...draft,
          timeFormat: e.target.value as LocalizationSettings['timeFormat']
        })} data-id="element-6828" data-mp="kko9axy5za93">
              <option value="24h" data-id="element-6829" data-mp="3o1wpldd8u21p">14:30</option>
              <option value="12h" data-id="element-6830" data-mp="3nz0qvmtkn9c7">2:30 pm</option>
            </Select>
          </Field>
          <Field label="Week starts on" data-id="element-6831" data-mp="3syn07l6a45fq">
            <Select value={draft.weekStartsOn} onChange={e => set({
          ...draft,
          weekStartsOn: e.target.value as LocalizationSettings['weekStartsOn']
        })} data-id="element-6832" data-mp="1yjq0vh3ekcic">
              <option value="monday" data-id="element-6833" data-mp="28bm1ozwvigq9">Monday</option>
              <option value="sunday" data-id="element-6834" data-mp="23i195ne1ueyg">Sunday</option>
            </Select>
          </Field>
          <Field label="Measurements" hint="Used for distance to campus." data-id="element-6835" data-mp="2h9plnrs7n5fy">
            <Select value={draft.measurement} onChange={e => set({
          ...draft,
          measurement: e.target.value as LocalizationSettings['measurement']
        })} data-id="element-6836" data-mp="2d95owpbju3uc">
              <option value="metric" data-id="element-6837" data-mp="2m9ainwntl3q1">Metric (km)</option>
              <option value="imperial" data-id="element-6838" data-mp="2m7r72jd3zgul">Imperial (mi)</option>
            </Select>
          </Field>
        </div>}
    </SectionForm>;
}

/* ----------------------------------------------------------- 5. security */

function SecuritySection() {
  const {
    security
  } = usePlatformConfig();
  return <div className="space-y-6" data-id="element-6839" data-mp="38hzjl83olbvu">
      <Panel title="Secrets" description="Generated during installation and held server-side." data-id="element-6840" data-mp="38i9107anuapx">
        <p className="text-body-sm text-ink-muted" data-id="element-6841" data-mp="2ttuzbzozcsay">
          The session secret and encryption key were generated from a cryptographic random source at
          installation. They are not shown here, and no endpoint returns them — anything that can be
          displayed can be captured. Rotating them is a server-side operation, deliberately not a
          button in a browser.
        </p>
      </Panel>

      <SectionForm<SecuritySettings> section="security" permission="platform.security.manage" title="Security policy" description="Applies to every account on this deployment. Tightening it does not sign anyone out; it applies at their next sign-in." live={security} validate={draft => draft.passwordMinLength < 8 ? 'A password floor below 8 characters is not defensible. 12 or more is recommended.' : null} summarise={draft => diffOf(security as never, draft as never)} data-id="element-6842" data-mp="2avac4iq7m72l">
        {(draft, set) => <div className="space-y-4" data-id="element-6843" data-mp="2ndiv7mt0he4">
            <div className="grid gap-4 md:grid-cols-2" data-id="element-6844" data-mp="2of424xjak9k">
              <Field label="Session length (hours)" data-id="element-6845" data-mp="3urpsgf59hqf5">
                <Input type="number" value={draft.sessionHours} onChange={e => set({
              ...draft,
              sessionHours: Number(e.target.value) || 12
            })} data-id="element-6846" data-mp="vb9l0rv571ia" />
              </Field>
              <Field label="Minimum password length" data-id="element-6847" data-mp="lkeqz1bnmxma">
                <Input type="number" value={draft.passwordMinLength} onChange={e => set({
              ...draft,
              passwordMinLength: Number(e.target.value) || 12
            })} data-id="element-6848" data-mp="hjuttxr2zu05" />
              </Field>
              <Field label="Failed sign-ins before throttling" data-id="element-6849" data-mp="1997ov3bu7fjj">
                <Input type="number" value={draft.loginAttemptsBeforeThrottle} onChange={e => set({
              ...draft,
              loginAttemptsBeforeThrottle: Number(e.target.value) || 5
            })} data-id="element-6850" data-mp="2rz3rgxwtgiaz" />
              </Field>
              <Field label="API requests per minute" data-id="element-6851" data-mp="35z4v2l4gu3u5">
                <Input type="number" value={draft.apiRequestsPerMinute} onChange={e => set({
              ...draft,
              apiRequestsPerMinute: Number(e.target.value) || 120
            })} data-id="element-6852" data-mp="3awwo5igujvzx" />
              </Field>
              <Field label="Cookie policy" data-id="element-6853" data-mp="3ontcgx3qpr16">
                <Select value={draft.sameSite} onChange={e => set({
              ...draft,
              sameSite: e.target.value as SecuritySettings['sameSite']
            })} data-id="element-6854" data-mp="3omrqhxl6rk4o">
                  <option value="lax" data-id="element-6855" data-mp="2ei9oppc8oqe">SameSite=Lax</option>
                  <option value="strict" data-id="element-6856" data-mp="39hsax4m3e0c6">SameSite=Strict</option>
                </Select>
              </Field>
            </div>
            <label className="flex items-center gap-2" data-id="element-6857" data-mp="34m3a6xyc6dea">
              <input type="checkbox" className="h-5 w-5" checked={draft.requireMfaForAdmins} onChange={e => set({
            ...draft,
            requireMfaForAdmins: e.target.checked
          })} data-id="element-6858" data-mp="3iiqitxznis01" />
              <span className="text-body text-ink" data-id="element-6859" data-mp="2qqhkspo6xrlc">Require multi-factor for administrators</span>
            </label>
            <label className="flex items-center gap-2" data-id="element-6860" data-mp="2zkw20xsa7c73">
              <input type="checkbox" className="h-5 w-5" checked={draft.secureCookies} onChange={e => set({
            ...draft,
            secureCookies: e.target.checked
          })} data-id="element-6861" data-mp="2zmgumhv1cl2b" />
              <span className="text-body text-ink" data-id="element-6862" data-mp="232zs8rx3qphb">
                Secure cookies only — refuse to set a session over plain HTTP
              </span>
            </label>
          </div>}
      </SectionForm>
    </div>;
}

/* -------------------------------------------------------- 6. admin access */

function AdminAccessSection() {
  const {
    config
  } = usePlatformConfig();
  const verdict = checkAdminSlug(config.adminSlug);
  return <div className="space-y-6" data-id="element-6863" data-mp="1o11csyrhw5i6">
      <Panel title="Where administration answers" description="Chosen at installation, changeable here." data-id="element-6864" data-mp="1nxvnpjiv93mg">
        <p className="cz-num text-h3 text-ink" data-id="element-6865" data-mp="2bnhpmeqmhwy5">/{config.adminSlug}</p>
        <p className="mt-2 text-body-sm text-ink-muted" data-id="element-6866" data-mp="2go5b6kw31lkz">
          A private administration URL keeps this console out of automated scans. It is obscurity,
          not authorisation: reaching it by any address still requires a provisioned internal staff
          link, authentication, and the permission for the action being attempted.
        </p>
        {!verdict.ok && <p className="mt-2 text-body-sm text-danger-700" data-id="element-6867" data-mp="vcdn8qzpqtyt">
            The stored slug is no longer valid: {verdict.reason}
          </p>}
      </Panel>

      <SectionForm<{
      adminSlug: string;
    }> section="admin-route" permission="platform.route.manage" title="Administration URL" description="The previous URL stops working the moment this is saved. Anyone with it open keeps their session but must use the new address." live={{
      adminSlug: config.adminSlug
    }} validate={draft => checkAdminSlug(draft.adminSlug).reason} summarise={draft => draft.adminSlug === config.adminSlug ? [] : [{
      field: 'adminSlug',
      from: `/${config.adminSlug}`,
      to: `/${draft.adminSlug}`
    }]} data-id="element-6868" data-mp="3t21eok5zy5b6">
        {(draft, set) => <div className="space-y-4" data-id="element-6869" data-mp="3k24vuz6ag0o1">
            <Field label="New administration URL" hint="Lower-case letters, numbers and hyphens. Something guessable defeats the point." data-id="element-6870" data-mp="3k36ateqfd7b1">
              <div className="flex items-center gap-2" data-id="element-6871" data-mp="2rj7l5xa5sghs">
                <span className="text-body text-ink-muted" data-id="element-6872" data-mp="2k1t1rwrs703o">/</span>
                <Input value={draft.adminSlug} onChange={event => set({
              adminSlug: event.target.value.trim().toLowerCase()
            })} data-id="element-6873" data-mp="2f2gdr8p6bmzi" />
              </div>
            </Field>
            <div className="rounded-lg border border-warn-200 bg-warn-50 p-4" data-id="element-6874" data-mp="253qubbhmpuh5">
              <p className="text-body-sm font-semibold text-ink" data-id="element-6875" data-mp="213qfvquj5ja3">Before you save this</p>
              <p className="mt-1 text-body-sm text-ink-muted" data-id="element-6876" data-mp="1wbovfhn7km8z">
                Write the new address down. If it is lost, recovery is deliberately not available
                through any public screen — a recovery screen would hand the address to whoever asked
                for it. It is restored on the server:
              </p>
              <pre className="mt-2 overflow-x-auto rounded bg-white p-2 text-caption text-ink" data-id="element-6877" data-mp="1hdwj0ycykdz0">
{`npm run admin:reset-route            # prints the current slug
npm run admin:reset-route -- <slug>   # sets a new one`}
              </pre>
            </div>
          </div>}
      </SectionForm>

      <Panel title="Reserved addresses" description="These cannot be chosen, because something else already answers there." data-id="element-6878" data-mp="17sdfr4bks66w">
        <div className="flex flex-wrap gap-1.5" data-id="element-6879" data-mp="ttnfxgxdfseu">
          {RESERVED_ROUTES.map(route => <span key={route} className="cz-num rounded border border-line px-1.5 py-0.5 text-caption text-ink-muted" data-id="element-6880" data-mp="ys8ihn03io6g">
              /{route}
            </span>)}
        </div>
      </Panel>
    </div>;
}

/* ------------------------------------------------------------ 7. database */

function DatabaseSection() {
  const [health, setHealth] = useState<DatabaseHealth | null>(null);
  const [loading, setLoading] = useState(false);
  const check = React.useCallback(async () => {
    setLoading(true);
    try {
      setHealth(await databaseHealth());
    } catch (error) {
      setHealth(null);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    check();
  }, [check]);
  return <PermissionGate permission="platform.settings.manage" label="view database health" data-id="element-6881" data-mp="2fhznyzslwn3r">
      <div className="space-y-6" data-id="element-6882" data-mp="2t94ftfpqgb3j">
        <Panel title="Database" description="Health and schema state. Credentials belong to deployment, not to a browser." action={<Button variant="secondary" icon={<ActivityIcon className="h-4 w-4" data-id="element-6885" data-mp="1wk6jht2c4549" />} onClick={check} loading={loading} data-id="element-6884" data-mp="20mjrko4k0f47">
              Test connection
            </Button>} data-id="element-6883" data-mp="2tca44sqjf8y7">
          {health ? <>
              <div className="flex flex-wrap items-center gap-3" data-id="element-6886" data-mp="25jjyf1989lwd">
                <Badge tone={health.connected ? 'ok' : 'danger'} data-id="element-6887" data-mp="3lyl544i23bvj">
                  {health.connected ? 'Connected' : 'Not connected'}
                </Badge>
                <p className="min-w-0 text-body-sm text-ink-muted" data-id="element-6888" data-mp="3gzibrhbfuu3e">{health.message}</p>
              </div>
              <dl className="mt-4 divide-y divide-line rounded-lg border border-line" data-id="element-6889" data-mp="1qkr5nolhr7w4">
                {[['Engine', health.engine], ['Server version', health.serverVersion ?? '—'], ['Host', health.hostMasked], ['Database', health.databaseName], ['Schema version', health.schemaVersion ?? '—'], ['Last migration', health.lastMigration ?? '—'], ['Pending migrations', health.pendingMigrations.length ? health.pendingMigrations.join(', ') : 'None outstanding'], ['Round trip', health.latencyMs === null ? '—' : `${health.latencyMs} ms`]].map(([term, value]) => <div key={term} className="grid gap-1 p-3 sm:grid-cols-[200px_1fr]" data-id="element-6890" data-mp="2x6tm75j6cz9k">
                    <dt className="text-body-sm font-semibold text-ink" data-id="element-6891" data-mp="ljq723px8mmi">{term}</dt>
                    <dd className="cz-num min-w-0 break-words text-body-sm text-ink-muted" data-id="element-6892" data-mp="lhn8gvapdl66">
                      {value}
                    </dd>
                  </div>)}
              </dl>
            </> : <p className="text-body-sm text-ink-muted" data-id="element-6893" data-mp="7ki18rz96g9c">
              <DatabaseIcon aria-hidden className="mr-2 inline h-4 w-4" data-id="element-6894" data-mp="7m1m6vvutfhg" />
              {loading ? 'Checking…' : 'No database report is available.'}
            </p>}
        </Panel>

        <Panel title="What this screen cannot show" description="Not hidden — absent." data-id="element-6895" data-mp="109fjiv3gsd23">
          <ul className="space-y-2 text-body-sm text-ink-muted" data-id="element-6896" data-mp="vgcqco2ln5mf">
            <li data-id="element-6897" data-mp="qjmb22ymce22">The database password, and the connection string that contains it.</li>
            <li data-id="element-6898" data-mp="1nyqe1uu81b7s">The session secret and the encryption key.</li>
            <li data-id="element-6899" data-mp="1o0bajq03j48a">
              Any credential-changing action. Database configuration is deployment configuration; it
              is changed on the server and the service restarted.
            </li>
          </ul>
        </Panel>

        <Panel title="Backup and restore" description="Architected, deliberately not exposed here." data-id="element-6900" data-mp="272k45j3nfkxd">
          <p className="text-body-sm text-ink-muted" data-id="element-6901" data-mp="1tadlnqd0x1ur">
            Backup, restore, configuration export and import run as server-side operations with their
            own authorisation. A restore reachable from a browser session is a single compromised
            session away from replacing the platform's data, so it is not offered here.
          </p>
        </Panel>
      </div>
    </PermissionGate>;
}

/* ------------------------------------------------------ 8. system information */

function SystemSection() {
  const {
    config,
    source
  } = usePlatformConfig();
  const [info, setInfo] = useState<SystemInformation | null>(null);
  useEffect(() => {
    systemInformation().then(setInfo).catch(() => setInfo(null));
  }, []);
  return <div className="space-y-6" data-id="element-6902" data-mp="x4tuskeyg1ma">
      <Panel title="System information" description="What this deployment is running." data-id="element-6903" data-mp="x5vgrjxie8is">
        <dl className="divide-y divide-line rounded-lg border border-line" data-id="element-6904" data-mp="t41ifiasdgls">
          {[['Platform', info?.platformVersion ?? 'Campuzon 2.0'], ['Front end', info?.frontendVersion ?? 'Campuzon 2.0'], ['API', info?.apiVersion ?? 'Not reachable'], ['Runtime', info?.runtimeVersion ?? '—'], ['Database', info?.databaseVersion ?? '—'], ['Schema', info?.schemaVersion ?? config.schemaVersion ?? '—'], ['Installed', info?.installedAt ?? (config.installed ? '—' : 'Not installed')], ['Environment', info?.environment ?? config.environment], ['API origin', info?.apiOrigin ?? 'Not configured'], ['Configuration source', source === 'server' ? 'Configuration service' : source === 'local' ? 'This browser' : 'Development defaults']].map(([term, value]) => <div key={term} className="grid gap-1 p-3 sm:grid-cols-[200px_1fr]" data-id="element-6905" data-mp="31pbrbw562qux">
              <dt className="text-body-sm font-semibold text-ink" data-id="element-6906" data-mp="35qnapjxg6fuo">{term}</dt>
              <dd className="cz-num min-w-0 break-words text-body-sm text-ink-muted" data-id="element-6907" data-mp="3aios1k9h2wrk">{value}</dd>
            </div>)}
        </dl>
        <p className="mt-3 flex items-start gap-2 text-caption text-ink-subtle" data-id="element-6908" data-mp="3fg6xv1s70vvc">
          <InfoIcon aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-6909" data-mp="2isanwfr9gyt3" />
          Secrets, private keys and authentication tokens are not part of this report. There is no
          field in it capable of carrying one.
        </p>
      </Panel>
    </div>;
}

/* ---------------------------------------------------------- terminology */

/**
 * The platform's business vocabulary.
 *
 * Semantic rather than find-and-replace: each concept carries its own singular,
 * plural and short forms, because "Property" does not pluralise by appending an
 * "s" and a collapsed sidebar needs a shorter word than a page heading.
 *
 * Renaming a concept changes labels only. Route ids, permissions and API
 * references are keyed to the technical id and are stated here as unaffected,
 * so nobody has to wonder whether renaming will break their bookmarks.
 */
function TerminologySection() {
  const {
    config
  } = usePlatformConfig();
  const live = config.terminology ?? {};
  return <div className="space-y-6" data-id="element-6910" data-mp="1ds2kcylafels">
      <Panel title="Why this exists" description="Business-facing words are configuration, not source code." data-id="element-6911" data-mp="1dt403gd90p9u">
        <p className="text-body-sm text-ink-muted" data-id="element-6912" data-mp="18vc9qqs95b7r">
          Every label in administration that names a business concept is resolved through this
          register. Rename <span className="font-semibold text-ink" data-id="element-6913" data-mp="ywd8ht9tghus">Housing</span> to{' '}
          <span className="font-semibold text-ink" data-id="element-6914" data-mp="2y8pg0ljxd9bi">Lodging</span> and the navigation, page titles,
          empty states and reports follow, with no code change and no rebuild.
        </p>
        <p className="mt-2 text-body-sm text-ink-muted" data-id="element-6915" data-mp="3hq8cbtbq7ai2">
          Addresses do not change. <span className="cz-num" data-id="element-6916" data-mp="3dowgv7667v1y">/admin/housing</span> stays exactly where
          it is, and permissions keep their existing keys — a label and an identifier are different
          things, so bookmarks, links and integrations are unaffected.
        </p>
      </Panel>

      <SectionForm<TerminologyOverrides> section="terminology" permission="platform.settings.manage" title="Platform terminology" description="Leave a field empty to use the shipped word. An empty field is not a blank label — it is the default." live={live} validate={draft => {
      for (const concept of TERM_CONCEPTS) {
        for (const form of ['singular', 'plural', 'short'] as const) {
          const value = draft[concept]?.[form];
          if (value && value.trim().length > 48) {
            return `${TERMINOLOGY[concept].singular}: a ${form} form longer than 48 characters will not fit the surfaces that use it.`;
          }
        }
      }
      return null;
    }} summarise={draft => {
      const changes: Array<{
        field: string;
        from: string;
        to: string;
      }> = [];
      for (const concept of TERM_CONCEPTS) {
        for (const form of ['singular', 'plural', 'short'] as const) {
          const before = resolveTerm(live, concept, form);
          const after = resolveTerm(draft, concept, form);
          if (before !== after) {
            changes.push({
              field: `${concept}.${form}`,
              from: before,
              to: after
            });
          }
        }
      }
      return changes;
    }} data-id="element-6917" data-mp="33sjp4eu6rr9u">
        {(draft, set) => <div className="space-y-3" data-id="element-6918" data-mp="1o6pwfn3d4roe">
            {TERM_CONCEPTS.map(concept => <ConceptRow key={concept} concept={concept} draft={draft} onChange={next => set(next)} data-id="element-6919" data-mp="1t7nc9t0wh9mq" />)}
          </div>}
      </SectionForm>
    </div>;
}
function ConceptRow({
  concept,
  draft,
  onChange
}: {
  concept: TermConcept;
  draft: TerminologyOverrides;
  onChange: (next: TerminologyOverrides) => void;
}) {
  const definition = TERMINOLOGY[concept];
  const overrides = draft[concept] ?? {};
  const overridden = (['singular', 'plural', 'short'] as const).some(form => Boolean(overrides[form]?.trim()));
  const set = (form: 'singular' | 'plural' | 'short', value: string) => {
    const next = {
      ...draft,
      [concept]: {
        ...overrides,
        [form]: value
      }
    };
    // A cleared field must mean "use the default", so the override is removed
    // rather than stored as an empty string that could render as a blank label.
    const forms = next[concept] as Record<string, string>;
    for (const key of Object.keys(forms)) if (!forms[key]?.trim()) delete forms[key];
    if (Object.keys(forms).length === 0) delete next[concept];
    onChange(next);
  };
  const reset = () => {
    const next = {
      ...draft
    };
    delete next[concept];
    onChange(next);
  };
  return <div className="rounded-lg border border-line p-4" data-id="element-6920" data-mp="2hi1tmmgzkpce">
      <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-6921" data-mp="2hhi9r07rzqvq">
        <div className="min-w-0" data-id="element-6922" data-mp="23ptxmwxj12er">
          <p className="text-body font-semibold text-ink" data-id="element-6923" data-mp="23osncuoc4jy3">
            {resolveTerm(draft, concept, 'singular')}
          </p>
          <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-6924" data-mp="1tsz3eq8ado6a">{definition.meaning}</p>
          <p className="mt-1 text-caption text-ink-subtle" data-id="element-6925" data-mp="1tv201t3y2fjz">
            Identifier <span className="cz-num" data-id="element-6926" data-mp="1yt3lulsr8sz5">{definition.id}</span> — never changes, whatever this
            is called.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2" data-id="element-6927" data-mp="1pquc3y5b93mt">
          {overridden ? <>
              <Badge tone="info" plain data-id="element-6928" data-mp="3f6bnufaj68sy">
                Renamed
              </Badge>
              <Button variant="ghost" size="sm" onClick={reset} data-id="element-6929" data-mp="3k54ze269bpqh">
                Reset to default
              </Button>
            </> : <Badge tone="neutral" plain data-id="element-6930" data-mp="1k1kp4op34xl6">
              Default
            </Badge>}
        </div>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-3" data-id="element-6931" data-mp="1f4ls9ye1ecja">
        {([['singular', 'Singular'], ['plural', 'Plural'], ['short', 'Short form']] as const).map(([form, label]) => <Field key={form} label={label} hint={`Default: ${definition[form]}`} data-id="element-6932" data-mp="114jgmtwbofch">
            <Input value={overrides[form] ?? ''} placeholder={definition[form]} onChange={event => set(form, event.target.value)} data-id="element-6933" data-mp="wdb8002tgy0d" />
          </Field>)}
      </div>
    </div>;
}

/* -------------------------------------------------------------- history */

function HistoryPanel() {
  const [changes, setChanges] = useState<SettingsChange[]>([]);
  useEffect(() => {
    settingsHistory().then(setChanges).catch(() => setChanges([]));
  }, []);
  return <Panel title="Configuration history" description="Append-only, with the previous and new values. Passwords and secrets are never recorded, because they are never received." data-id="element-6934" data-mp="2p2lo4aokhkvb">
      {changes.length === 0 ? <p className="text-body-sm text-ink-muted" data-id="element-6935" data-mp="2y30brgp07c1z">
          <KeyRoundIcon aria-hidden className="mr-2 inline h-4 w-4" data-id="element-6936" data-mp="3323f8wpc5a7u" />
          No configuration changes have been recorded on this deployment yet.
        </p> : <ul className="space-y-3" data-id="element-6937" data-mp="269hpeawhshtu">
          {changes.map(change => <li key={change.id} className="rounded-lg border border-line p-3" data-id="element-6938" data-mp="2ad5wytl4jdf3">
              <div className="flex flex-wrap items-center gap-2" data-id="element-6939" data-mp="2fb7fnden1aq1">
                <Badge tone="neutral" plain data-id="element-6940" data-mp="2k38zbke4y3q3">
                  {label(change.section)}
                </Badge>
                <span className="text-body-sm font-semibold text-ink" data-id="element-6941" data-mp="1mo3ifulse0ot">{change.changedBy}</span>
                <span className="cz-num text-caption text-ink-subtle" data-id="element-6942" data-mp="1rlwpepuwyyq5">
                  {new Date(change.createdAt).toLocaleString()}
                </span>
              </div>
              <p className="mt-1 text-body-sm text-ink-muted" data-id="element-6943" data-mp="2py8wkeictws6">{change.reason}</p>
              <ul className="mt-2 space-y-1" data-id="element-6944" data-mp="2pw4snwkggxrw">
                {change.changed.map(field => <li key={field.field} className="text-caption text-ink-muted" data-id="element-6945" data-mp="33rqe5trhczfk">
                    <span className="font-semibold text-ink" data-id="element-6946" data-mp="2zou5aurqshot">{label(field.field)}</span>{' '}
                    <span className="line-through" data-id="element-6947" data-mp="2byzs2c3igr41">{field.from}</span> → {field.to}
                  </li>)}
              </ul>
            </li>)}
        </ul>}
    </Panel>;
}